import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { 
  ClipboardCheck, 
  Search, 
  Calendar,
  User,
  MapPin,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Package,
  Sparkles,
  Filter
} from "lucide-react";
import { toast } from "sonner";

export default function ChecklistsPage() {
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [filterDate, setFilterDate] = useState("");
  const [workers, setWorkers] = useState([]);
  const [filterWorker, setFilterWorker] = useState("");

  useEffect(() => {
    fetchChecklists();
    fetchWorkers();
  }, []);

  const fetchWorkers = async () => {
    try {
      const response = await axios.get(`${API}/workers`);
      setWorkers(response.data);
    } catch (error) {
      console.error("Error fetching workers:", error);
    }
  };

  const fetchChecklists = async () => {
    try {
      let url = `${API}/checklists`;
      const params = [];
      if (filterDate) params.push(`date=${filterDate}`);
      if (filterWorker) params.push(`worker_id=${filterWorker}`);
      if (params.length > 0) url += `?${params.join("&")}`;
      
      const response = await axios.get(url);
      setChecklists(response.data);
    } catch (error) {
      toast.error("Erro ao carregar checklists");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading) {
      fetchChecklists();
    }
  }, [filterDate, filterWorker]);

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const hasIssues = (checklist) => {
    return !checklist.work_ok || checklist.material_missing || !checklist.areas_cleaned;
  };

  const getStatusBadge = (checklist) => {
    if (hasIssues(checklist)) {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 flex items-center gap-1">
          <AlertTriangle size={12} />
          Com problemas
        </span>
      );
    }
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 flex items-center gap-1">
        <CheckCircle size={12} />
        Tudo OK
      </span>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in" data-testid="checklists-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 font-manrope">Checklists</h1>
            <p className="text-gray-500 mt-1">{checklists.length} checklist(s) respondido(s)</p>
          </div>
        </div>

        {/* Filters */}
        <Card className="bg-white border-gray-100 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm text-gray-600 mb-1 block">Filtrar por data</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="date"
                    value={filterDate}
                    onChange={(e) => setFilterDate(e.target.value)}
                    className="pl-10 bg-gray-50 border-gray-200"
                  />
                </div>
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm text-gray-600 mb-1 block">Filtrar por trabalhador</label>
                <select
                  value={filterWorker}
                  onChange={(e) => setFilterWorker(e.target.value)}
                  className="w-full h-10 px-3 bg-gray-50 border border-gray-200 rounded-md text-gray-800"
                >
                  <option value="">Todos</option>
                  {workers.map((worker) => (
                    <option key={worker.id} value={worker.id}>
                      {worker.name}
                    </option>
                  ))}
                </select>
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  setFilterDate("");
                  setFilterWorker("");
                }}
                className="border-gray-200 text-gray-600"
              >
                Limpar Filtros
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Checklists List */}
        {checklists.length === 0 ? (
          <Card className="bg-white border-gray-100 shadow-sm">
            <CardContent className="py-16">
              <div className="text-center">
                <ClipboardCheck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg font-medium">Nenhum checklist encontrado</p>
                <p className="text-gray-400 text-sm mt-1">
                  Os checklists aparecerão aqui quando os trabalhadores registrarem saída
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {checklists.map((checklist) => (
              <Card 
                key={checklist.id} 
                className={`bg-white border-gray-100 shadow-sm card-hover cursor-pointer ${
                  hasIssues(checklist) ? "border-l-4 border-l-amber-400" : ""
                }`}
                onClick={() => setSelectedChecklist(checklist)}
                data-testid={`checklist-${checklist.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        hasIssues(checklist) ? "bg-amber-100" : "bg-green-100"
                      }`}>
                        <ClipboardCheck className={`w-6 h-6 ${
                          hasIssues(checklist) ? "text-amber-600" : "text-green-600"
                        }`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <User size={14} className="text-gray-400" />
                          <span className="font-semibold text-gray-800">{checklist.worker_name}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <MapPin size={14} className="text-gray-400" />
                          <span className="text-sm text-gray-500">{checklist.location_name}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {getStatusBadge(checklist)}
                      <span className="text-sm text-gray-400">
                        {formatDate(checklist.created_at)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Quick summary of issues */}
                  {hasIssues(checklist) && (
                    <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-2">
                      {!checklist.work_ok && (
                        <span className="text-xs bg-red-50 text-red-600 px-2 py-1 rounded">
                          Problema no trabalho
                        </span>
                      )}
                      {checklist.material_missing && (
                        <span className="text-xs bg-amber-50 text-amber-600 px-2 py-1 rounded">
                          Material em falta
                        </span>
                      )}
                      {!checklist.areas_cleaned && (
                        <span className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded">
                          Áreas não limpas
                        </span>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Checklist Detail Modal */}
        <Dialog open={!!selectedChecklist} onOpenChange={() => setSelectedChecklist(null)}>
          <DialogContent className="bg-white border-gray-200 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-gray-800 flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5 text-[#F5A623]" />
                Detalhes do Checklist
              </DialogTitle>
            </DialogHeader>
            
            {selectedChecklist && (
              <div className="space-y-4 mt-4">
                {/* Worker & Location Info */}
                <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                  <div className="flex items-center gap-2">
                    <User size={16} className="text-gray-400" />
                    <span className="font-medium text-gray-800">{selectedChecklist.worker_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin size={16} className="text-gray-400" />
                    <span className="text-gray-600">{selectedChecklist.location_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar size={16} className="text-gray-400" />
                    <span className="text-sm text-gray-500">{formatDate(selectedChecklist.created_at)}</span>
                  </div>
                </div>

                {/* Question 1 */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700 font-medium">O trabalho decorreu sem problemas?</span>
                    {selectedChecklist.work_ok ? (
                      <span className="flex items-center gap-1 text-green-600">
                        <CheckCircle size={16} /> Sim
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-red-600">
                        <XCircle size={16} /> Não
                      </span>
                    )}
                  </div>
                  {!selectedChecklist.work_ok && selectedChecklist.work_issue && (
                    <div className="p-3 bg-red-50 rounded-lg text-sm text-red-700">
                      <strong>Motivo:</strong> {selectedChecklist.work_issue}
                    </div>
                  )}
                </div>

                {/* Question 2 */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700 font-medium">Falta material de consumo?</span>
                    {!selectedChecklist.material_missing ? (
                      <span className="flex items-center gap-1 text-green-600">
                        <CheckCircle size={16} /> Não
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-amber-600">
                        <Package size={16} /> Sim
                      </span>
                    )}
                  </div>
                  {selectedChecklist.material_missing && selectedChecklist.material_missing_desc && (
                    <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-700">
                      <strong>Material em falta:</strong> {selectedChecklist.material_missing_desc}
                    </div>
                  )}
                </div>

                {/* Question 3 */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700 font-medium">Todos os ambientes foram limpos?</span>
                    {selectedChecklist.areas_cleaned ? (
                      <span className="flex items-center gap-1 text-green-600">
                        <Sparkles size={16} /> Sim
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-blue-600">
                        <XCircle size={16} /> Não
                      </span>
                    )}
                  </div>
                  {!selectedChecklist.areas_cleaned && selectedChecklist.areas_not_cleaned && (
                    <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                      <strong>Áreas não limpas:</strong> {selectedChecklist.areas_not_cleaned}
                    </div>
                  )}
                </div>

                <Button
                  onClick={() => setSelectedChecklist(null)}
                  className="w-full btn-primary text-white font-semibold mt-4"
                >
                  Fechar
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
